import { Select } from 'antd';
import { SelectProps } from 'antd/lib/select';
import './VtxSelect.scss';
import React from 'react';
import { IAutomatable, preventDataAutomationOverriding } from '../../helpers/IAutomatable';
import classNames from 'classnames';
import { typedMemo } from '../../helpers/typedMemo';
import { arrowIconProp, baseSelectDataAutomationClass } from './commonSelectConcerns';

export interface VtxSelectProps<ValueType extends RawValue, Op extends OptionsType<ValueType> = never>
    extends Omit<SelectProps<ValueType>, 'mode' | 'suffixIcon' | 'menuItemSelectedIcon' | 'removeIcon'>,
        IAutomatable {
    options?: Op;
    onChange?: VtxSelectOnChange<ValueType>;
    /**
     * You can not specify this with VtxSelect. If you need to set this to "multiple" then you are probably looking for "VtxMultiSelect"
     */
    mode?:
        | undefined
        | 'This option has been removed-- if you are looking to set this to multiple, then please use VtxMultiSelect instead';
}
export interface VtxOptionProps<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue>
    extends OptionProps<TValue, TKey>,
        IAutomatable {
    children: React.ReactNode;
}

export type VtxSelectOnChange<VT extends RawValue> = (value: VT) => void;

export type VtxSelectOnSelect<VT extends RawValue, TKeyForOptions extends RawValue = string> = VtxSelectProps<
    VT,
    OptionsType<VT, TKeyForOptions>
>['onDeselect'];

export type VtxSelectOnDeselect<
    VT extends RawValue,
    TKeyForOptions extends RawValue = string
> = VtxSelectProps<VT, OptionsType<VT, TKeyForOptions>>['onChange'];

export const VtxSelect = typedMemo(
    <VT extends RawValue = RawValue, TKeyForOptions extends RawValue = string>(
        props: VtxSelectProps<VT, OptionsType<VT, TKeyForOptions>>
    ) => {
        const automationClass = baseSelectDataAutomationClass;
        const {
            className,
            mode,
            children,
            dropdownClassName,
            ...otherProps
        } = preventDataAutomationOverriding(props);
        const classNamesCombined = classNames(automationClass, props.className);

        const singleSelectValueMode: SelectProps<never>['mode'] = undefined;

        return (
            <Select
                {...arrowIconProp}
                data-automation-class={automationClass}
                className={classNamesCombined}
                dropdownClassName={classNamesCombined}
                mode={singleSelectValueMode}
                {...otherProps}
            >
                {props.children}
            </Select>
        );
    }
);

//TODO: removed wrapped option if https://github.com/react-component/select/pull/596 is accepted
const explanationOfUnfortunateAntDesignWarning = `You can safely ignore the a warning containing the following text if you see it in the console. It only show up in development. 
Here's the warning: "Warning: 'children' should be 'Select.Option' or 'Select.OptGroup' instead of [object Object]`;

export const VtxOption = typedMemo(
    <TValue extends RawValue = RawValue, TKey extends RawValue = RawValue>(
        props: VtxOptionProps<TValue, TKey>
    ) => {
        if (__DEV__) {
            // eslint-disable-next-line no-console
            console.warn(explanationOfUnfortunateAntDesignWarning);
        }
        return <Select.Option {...props}></Select.Option>;
    }
);

export const VtxOptGroup = Select.OptGroup;

// START OF THINGS TO DELETE
// TODO: delete this file if https://github.com/react-component/select/pull/596 is accepted

export type RawValue = string | number;
export declare interface OptionProps<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue>
    extends Omit<OptionCoreData<TValue, TKey>, 'label'> {
    children: React.ReactNode;
    /** Save for customize data */
    [prop: string]: unknown;
}

export interface OptionCoreData<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue> {
    key?: TKey;
    disabled?: boolean;
    value: TValue;
    title?: string;
    options?: undefined;
    className?: string;
    style?: React.CSSProperties;
    label?: React.ReactNode;
    /** @deprecated Only works when use `children` as option data */
    children?: React.ReactNode;
}

export interface OptionData<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue>
    extends OptionCoreData<TValue, TKey> {
    /** Save for customize data */
    [prop: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export interface OptionGroupData<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue> {
    key?: TKey;
    label?: React.ReactNode;
    options: Array<OptionData<TValue, TKey>>;
    className?: string;
    style?: React.CSSProperties;

    /** Save for customize data */
    [prop: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
}

export type OptionsType<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue> = Array<
    OptionData<TValue, TKey> | OptionGroupData<TValue, TKey>
>;

export interface FlattenOptionData<TValue extends RawValue = RawValue, TKey extends RawValue = RawValue> {
    group?: boolean;
    groupOption?: boolean;
    key: string | number;
    data: OptionData<TValue, TKey> | OptionGroupData<TValue, TKey>;
}
// END OF TODO: delete this file if https://github.com/react-component/select/pull/596 is accepted
